import type { Drawing, PathStyle } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import { useTheme } from '@/contexts/SettingsContext.tsx'
import { DialogCloseButton } from '../../ui/DialogCloseButton'
import { BRUSH_SIZES, getColorPresets } from './Dialog.constants'
import { BrushSizeSelector, LineEndSelector, LineStyleSelector, PathModeSelector } from './shared'
import './drawing-properties-dialog.css'

interface DrawingPropertiesDialogProps {
	drawing: Drawing
	position: { x: number; y: number }
	onUpdate: (_updates: Partial<PathStyle>) => void
	onClose: () => void
	coordSystem: FieldCoordinateSystem
	onAddPreSnapMovement?: () => void
	playerHasPreSnapMovement?: boolean
}

const DIALOG_WIDTH = 440
const DIALOG_MAX_HEIGHT = 360

export function DrawingPropertiesDialog({
											drawing,
											position,
											onUpdate,
											onClose,
											coordSystem,
											onAddPreSnapMovement,
											playerHasPreSnapMovement,
										}: DrawingPropertiesDialogProps) {
	const { style } = drawing
	const { theme } = useTheme()
	const colorPresets = getColorPresets(theme)

	// Show pre-snap movement button only if drawing is linked to a player
	const showPreSnapButton = !!drawing.playerId
	const hasPreSnapMotion = !!drawing.preSnapMotion

	// Position dialog near click, but keep on screen
	// Ensure dialog stays within viewport bounds
	const left = Math.min(
		Math.max(0, position.x),
		window.innerWidth - DIALOG_WIDTH - 16, // 16px margin
	)
	const top = Math.min(
		Math.max(0, position.y),
		window.innerHeight - DIALOG_MAX_HEIGHT - 16, // 16px margin
	)

	const dialogStyle = {
		position: 'fixed' as const,
		left,
		top,
	}

	return (
		<>
			{/* Transparent backdrop to handle click-away */}
			<div
				className="drawing-properties-dialog-backdrop"
				onClick={onClose}
			/>
			<div
				style={dialogStyle}
				className="drawing-properties-dialog"
			>
				{/* Header */}
				<div className="drawing-properties-dialog-header">
					<span className="drawing-properties-dialog-title">
						Edit Drawing
					</span>
					<DialogCloseButton onClose={onClose} />
				</div>

				{/* Two-column grid layout */}
				<div className="drawing-properties-grid">
					{/* Row 1: Color + Line Thickness */}
					<div className="drawing-properties-dialog-section">
						<label className="drawing-properties-dialog-label">
							Color
						</label>
						<div className="drawing-properties-color-grid">
							{colorPresets.map((preset) => (
								<button
									key={preset.value}
									onClick={() => onUpdate({ color: preset.value })}
									className="drawing-properties-color-button"
									data-selected={style.color === preset.value}
									style={{ backgroundColor: preset.value }}
									title={preset.name}
								/>
							))}
						</div>
					</div>

					<div className="drawing-properties-dialog-section">
						<label className="drawing-properties-dialog-label">
							Line Thickness
						</label>
						<BrushSizeSelector
							brushSize={style.brushSize ?? BRUSH_SIZES.reduce((closest, b) => {
								const pixelSize = style.strokeWidth * coordSystem.scale
								return Math.abs(pixelSize - b.size) < Math.abs(pixelSize - closest.size) ? b : closest
							}).size}
							sizes={BRUSH_SIZES}
							onChange={(sizeInPixels) => {
								onUpdate({
									brushSize: sizeInPixels,
									strokeWidth: sizeInPixels / coordSystem.scale,
								})
							}}
						/>
					</div>

					{/* Row 2: Line Style + Path Mode */}
					<div className="drawing-properties-dialog-section">
						<label className="drawing-properties-dialog-label">
							Line Style
						</label>
						<LineStyleSelector
							lineStyle={style.lineStyle}
							onChange={(lineStyle) => onUpdate({ lineStyle })}
						/>
					</div>

					<div className="drawing-properties-dialog-section">
						<label className="drawing-properties-dialog-label">
							Path Mode
						</label>
						<PathModeSelector
							pathMode={style.pathMode}
							onChange={(mode) => onUpdate({ pathMode: mode })}
						/>
					</div>

					{/* Row 3: Pre Snap + Line End */}
					<div className="drawing-properties-dialog-section">
						<label className="drawing-properties-dialog-label">
							Line End
						</label>
						<LineEndSelector
							lineEnd={style.lineEnd}
							onChange={(lineEnd) => onUpdate({ lineEnd })}
						/>
					</div>

					{showPreSnapButton && onAddPreSnapMovement ? (
						<div className="drawing-properties-dialog-section">
							<label className="drawing-properties-dialog-label">
								Pre-Snap Movement
							</label>
							<button
								onClick={() => {
									onAddPreSnapMovement()
									onClose()
								}}
								className="drawing-properties-presnap-button"
								disabled={playerHasPreSnapMovement && !hasPreSnapMotion}
								title={
									playerHasPreSnapMovement && !hasPreSnapMotion
										? 'Player already has pre-snap movement on another drawing'
										: hasPreSnapMotion
											? 'Click to remove pre-snap movement'
											: 'Click to add pre-snap movement'
								}
							>
								{hasPreSnapMotion ? 'Remove' : 'Add'}
							</button>
						</div>
					) : (
						<div className="drawing-properties-dialog-section" />
					)}
				</div>
			</div>
		</>
	)
}
